import React, { useMemo } from 'react'
import { traceConf, TraceKey } from '../../../system-charts/shared/config/traceConf'
import { ChartView, RechartSum, TraceSuffix } from '../utils/chartModel'
import { LineChartSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { Box, Typography } from '@mui/material'
import { getChartColor } from 'src/Libs/system-charts/shared/config/chartColors'
import { formatValue, getComparisonYears, getHoverValueWithcorrectUnit } from '../utils'
import { HoverPayload } from '../types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import LegendItem, { LegendItemProps } from './LegendItem'
import { isUndefined } from 'lodash'

interface Props {
  traceNames: Record<string, string>
  sums: Record<string, RechartSum>
  showForecast: boolean
  impossibleTraces: string[]
  disabledTraces: string[]
  hiddenTraces: string[]
  onTraceToggle: (trace: string) => void
  colors?: { key: string; color: string }[]
  defaultUnit?: string
  from: Moment
  hasSuffixes?: boolean
  summaryUnit?: string
  hoverPayload?: HoverPayload
  view: ChartView
  isWeatherBreakthroughDotVisible: boolean
  setIsWeatherBreakthroughDotVisible: React.Dispatch<React.SetStateAction<boolean>>
  isWeatherBreakthroughReached?: boolean
}

const viewLegendIconTypeMap: Record<ChartView, LegendItemProps['type']> = {
  hour: 'line',
  day: 'line',
  week: 'line',
  month: 'bar',
  year: 'bar',
}

const TRACES_WITHOUT_SUM: TraceKey[] = ['ASOC', 'Deration']

const Summary: React.FC<Props> = ({
  traceNames,
  sums,
  showForecast,
  impossibleTraces,
  hiddenTraces,
  onTraceToggle,
  colors,
  defaultUnit,
  from,
  hasSuffixes,
  hoverPayload,
  view,
  isWeatherBreakthroughDotVisible,
  setIsWeatherBreakthroughDotVisible,
  isWeatherBreakthroughReached,
}) => {
  const prevFrom = useMemo(() => moment(from).subtract(1, 'year'), [from])
  const suffixes = hasSuffixes ? getComparisonYears(from, prevFrom) : undefined
  const { t } = useCustomTranslation()

  /** Only show the breakthrough-legend-item if the summary contains the forecast-trace */
  const showWeatherBreakthroughLegendItem = isWeatherBreakthroughReached && Object.keys(traceNames).some(elem => elem === 'ForecastPower')

  return useMemo(() => {
    if (!sums) return <LineChartSkeleton />

    // Extracted handler to avoid deep nesting
    const handleWeatherBreakthroughLegendClick = (e: React.MouseEvent): void => {
      e.stopPropagation()
      setIsWeatherBreakthroughDotVisible((prev: boolean) => !prev)
    }

    const getSumsFromSuffixes = (suffixes: TraceSuffix[] | undefined): string[] => {
      const completeSumKeys = Object.keys(sums)
      const sumsToDisplay = hasSuffixes ? [] : Object.keys(traceNames)

      for (const suffix of suffixes ?? []) {
        for (const traceName of Object.keys(traceNames)) {
          const key = `${traceName}-${suffix.name}`
          completeSumKeys.push(key)
          sumsToDisplay.push(key)
        }
      }

      return sumsToDisplay
    }

    const getElementValueToDisplay = (sums: Record<string, RechartSum>, elementKey: string, unit: string): string => {
      const traceWithId = elementKey.startsWith('wb') || elementKey.startsWith('pm')
      let value: number | string = sums[elementKey]?.history ?? 0

      if (traceWithId && isUndefined(sums[elementKey])) {
        const elementKeyWithoutId = elementKey.split('/')[1] as TraceKey
        value += sums[elementKeyWithoutId]?.history ?? 0
      }

      if (showForecast && sums[elementKey] && 'forecast' in sums[elementKey] && sums[elementKey].forecast) {
        value += sums[elementKey].forecast ?? 0
      }

      value = formatValue(value, unit)
      const valueToDisplay = TRACES_WITHOUT_SUM.includes(elementKey as TraceKey) ? '' : value
      return valueToDisplay
    }

    const getElems = (sumsToDisplay: string[]): JSX.Element[] => {
      const elems = []

      for (const key of sumsToDisplay) {
        const keyToUse = hasSuffixes ? key.split('-')[0] : key

        if (impossibleTraces.includes(key)) continue

        const unit = defaultUnit ?? traceConf[key as TraceKey]?.unit ?? ''
        const valueToDisplay = getElementValueToDisplay(sums, key, unit)
        const hoverValue = getHoverValueWithcorrectUnit(hoverPayload?.activePayload, key, unit, showForecast, view)
        const colorKey = keyToUse.startsWith('wb') || keyToUse.startsWith('pm') ? keyToUse.split('/')[1] : key
        const nameKey = keyToUse
        const composedName = hasSuffixes ? `${traceNames[nameKey]}-${key.split('-')[1]}` : traceNames[nameKey]

        const legendIconType = key === 'ASOC' ? 'gradient' : viewLegendIconTypeMap[view] ?? 'gradient'

        elems.push(
          <LegendItem
            key={key}
            type={legendIconType}
            color={colors?.find(color => color.key === key)?.color ?? sums[key]?.customColor ?? getChartColor(colorKey as TraceKey)}
            title={composedName ?? key}
            value={hoverValue ?? valueToDisplay}
            dataQa={`chart-legend-value-${key}`}
            onClick={e => {
              e.stopPropagation()
              onTraceToggle(key)
            }}
            isDisabled={hiddenTraces.includes(key)}
          />
        )
      }

      /** WeatherBreakthroughItem */
      if (showWeatherBreakthroughLegendItem) {
        const isForecastTraceDisabled = hiddenTraces.includes('ForecastPower')
        elems.push(
          <LegendItem
            key={'WeatherBreakthrough'}
            type="rectangle"
            color={getChartColor('WeatherBreakthrough')}
            title={t('systemDataHandler.trace.WeatherBreakthrough.systemDataHandler.trace.unknown')}
            isDisabled={!isWeatherBreakthroughDotVisible || isForecastTraceDisabled}
            onClick={handleWeatherBreakthroughLegendClick}
          />
        )
      }

      return elems
    }

    const sumsToDisplay = getSumsFromSuffixes(suffixes)
    const elems = getElems(sumsToDisplay)

    const WeatherBreakthroughHintText = (
      <Typography
        variant="subtitle2"
        sx={theme => ({
          color: 'grey.grey03',
          gridColumn: '1/-1',
          mt: 2,

          [theme.breakpoints.down('sm')]: {
            pb: 1,
          },
        })}
      >
        {t('systemDataHandler.trace.breakthrough-hint')}
      </Typography>
    )

    return (
      <Box
        sx={theme => ({
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(120px, 1fr))`,
          gap: 2,
          width: '100%',
          height: '100%',
          alignItems: 'center',
          marginTop: 2,
          paddingTop: { xs: 0, sm: 2 },
          paddingBottom: { xs: 0, sm: 2 },
          justifyContent: 'flex-start',

          [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: `repeat(auto-fit, minmax(150px, max-content))`,
          },
        })}
      >
        {elems}
        {showWeatherBreakthroughLegendItem && WeatherBreakthroughHintText}
      </Box>
    )
  }, [
    sums,
    suffixes,
    t,
    showWeatherBreakthroughLegendItem,
    hasSuffixes,
    traceNames,
    showForecast,
    impossibleTraces,
    defaultUnit,
    hoverPayload?.activePayload,
    view,
    colors,
    hiddenTraces,
    onTraceToggle,
    isWeatherBreakthroughDotVisible,
    setIsWeatherBreakthroughDotVisible,
  ])
}

export default Summary
